import { hasOwnProperty } from "@glideapps/ts-necessities";

import { panic, isResponseOK } from "common/Support";
import { logInfo, logError } from "common/debug-print";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { TableColumn } from "common/generator/description";
import { TableName } from "common/database-strings";
import {
    GeneratePublishedAppDataFromSheetBody,
    SendShareSMSBody,
    UserDataUpdates,
    UserDataRemovals,
    SetAppPlanBody,
    ModifySyntheticColumnsBody,
    GeneratePublishedAppDataFromSheetResult,
    GetUnsplashImagesBody,
    UnsplashPingDownloadBody,
    TransferAppToOrganizationBody,
    TransferAppToOrganizationResponse,
    SetAppPlanUnifiedBody,
    ReloadPublishedAppDataFromSheetBody,
    SetOrgOnboardingBody,
    FinishOnboardingBody,
    NewAppSurveyData,
    MoveTableInSchemaBody,
} from "common/firebase-function-types";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import { AppPlanCode } from "common/app-plans";

let referredBy: string | undefined;

export function setReferralID(referrer: string): void {
    referredBy = referrer;
}

export function getReferralID(): string | undefined {
    return referredBy;
}

export async function saveUserUpdates(updates: UserDataUpdates, removals: Partial<UserDataRemovals>): Promise<boolean> {
    logInfo("updating user", updates);
    const response = await getAppFacilities().callAuthCloudFunction("setOrUpdateUser", {
        updates,
        removals,
        referredBy,
    });
    // FIXME: We should handle this case in a much more graceful fashion.
    if (!isResponseOK(response)) {
        return panic("Could not save user updates");
    }
    const { created } = await response.json();
    return created;
}

export async function generatePublishedAppDataFromSheet(
    sheetsFileID: string | undefined,
    organizationID: string | undefined,
    newAppSurveyData: NewAppSurveyData
): Promise<GeneratePublishedAppDataFromSheetResult> {
    const body: GeneratePublishedAppDataFromSheetBody = { sheetsFileID, organizationID, ...newAppSurveyData };
    const response = await getAppFacilities().callAuthCloudFunction("generatePublishedAppDataFromSheet", body);
    if (response === undefined) return "Network error";
    const json = await response.json();
    if (!response.ok) {
        logError("Could not generate published app data", response);
        if (typeof json.error === "string") {
            return json.error;
        }
        return "An error occurred.";
    }

    return json;
}

async function callModifySyntheticColumns(body: ModifySyntheticColumnsBody): Promise<boolean> {
    const response = await getAppFacilities().callAuthCloudFunction("modifySyntheticColumns", body);
    return isResponseOK(response);
}

export async function setSyntheticColumn(
    appID: string,
    hostTableName: TableName,
    column: TableColumn,
    isRowID: boolean,
    confirmDestructive: boolean
): Promise<boolean> {
    const body: ModifySyntheticColumnsBody = { appID, set: { [hostTableName.name]: [column] }, confirmDestructive };
    if (isRowID) {
        body.rowIDColumns = [[hostTableName.name, column.name]];
    }
    return await callModifySyntheticColumns(body);
}

export async function setRowIDColumn(
    appID: string,
    hostTableName: TableName,
    rowIDColumnName: string
): Promise<boolean> {
    return await callModifySyntheticColumns({ appID, rowIDColumns: [[hostTableName.name, rowIDColumnName]] });
}

export async function removeSyntheticColumn(
    appID: string,
    tableName: TableName,
    column: string,
    allowDeletingSheetColumns: boolean
): Promise<boolean> {
    return await callModifySyntheticColumns({
        appID,
        remove: {
            [tableName.name]: [column],
        },
        confirmDestructive: allowDeletingSheetColumns,
    });
}

export async function moveSyntheticColumn(
    appID: string,
    tableName: TableName,
    column: string,
    index: number
): Promise<boolean> {
    return await callModifySyntheticColumns({
        appID,
        move: {
            [tableName.name]: [[column, index]],
        },
    });
}

export async function setColumnProtected(
    appID: string,
    tableName: TableName,
    column: string,
    setProtected: boolean
): Promise<boolean> {
    return await callModifySyntheticColumns({
        appID,
        setProtected: {
            [tableName.name]: [[column, setProtected]],
        },
    });
}

export async function moveTableInSchema(
    appID: string,
    tableName: TableName,
    afterTableName: TableName | undefined
): Promise<boolean> {
    const body: MoveTableInSchemaBody = { appID, tableName, afterTableName };
    const response = await getAppFacilities().callAuthCloudFunction("moveTableInSchema", body);
    return isResponseOK(response);
}

const defaultSMSErrorMessage = "Something went wrong sending your SMS.";

// If a string is returned, it's an error message
export async function sendShareSMS(appID: string, phoneNumber: string): Promise<string | undefined> {
    const body: SendShareSMSBody = { appID, phoneNumber };
    const response = await callCloudFunctionWeb("sendShareSMS", body);
    if (isResponseOK(response)) return undefined;
    if (response === undefined) {
        return defaultSMSErrorMessage;
    }
    const r = await response.json();
    if (hasOwnProperty(r, "error") && typeof r.error === "string") {
        return r.error;
    }
    return defaultSMSErrorMessage;
}

export interface UnsplashResult {
    id: string;
    urls: {
        raw: string;
        thumb: string;
        regular: string;
    };
    user: {
        name: string;
        links: {
            html: string;
        };
    };
}
interface UnsplashResults {
    error: string | undefined;
    results: UnsplashResult[] | undefined;
}

export async function getUnsplashImages(appID: string, query: string): Promise<UnsplashResults> {
    const body: GetUnsplashImagesBody = { appID, query };
    const response = await getAppFacilities().callAuthCloudFunction("getUnsplashImages", body);

    // Handle function error
    if (!isResponseOK(response)) {
        return { results: undefined, error: "Oh sheet!" };
    }
    const result = await response.json();
    if (hasOwnProperty(result, "errors")) {
        // If API request to unsplash fails, the response will have an errors prop
        return { results: undefined, error: "Oh sheet! Please try again!" };
    }
    if (!hasOwnProperty(result, "results")) {
        // Handle if results does not exist on payload
        return { results: undefined, error: "Oh sheet! Please try again!" };
    }
    return { results: result.results as any, error: undefined };
}

export async function pingUnsplashDownload(appID: string, imageId: string): Promise<void> {
    const body: UnsplashPingDownloadBody = { appID, imageId };
    await getAppFacilities().callAuthCloudFunction("pingUnsplashDownload", body);
}

export async function setAppPlan(appID: string, plan: "free" | "pro"): Promise<{ ok: boolean; status: number }> {
    const body: SetAppPlanBody = { appID, plan };
    const response = await getAppFacilities().callAuthCloudFunction("setAppPlan", body);
    // FIXME: This is a bit of a hack. We should have a more formal way to do this.
    if (response === undefined) return { ok: false, status: 500 };
    const { ok, status } = response;
    return { ok, status };
}

export async function setAppPlanUnified(
    appID: string,
    plan: AppPlanCode,
    numUsers: number | undefined,
    attachPaymentMethodAsDefault: string | undefined
): Promise<{ ok: boolean; status: number; setupIntentClientSecret?: string }> {
    const body: SetAppPlanUnifiedBody = { appID, plan, attachPaymentMethodAsDefault, numUsers };
    const response = await getAppFacilities().callAuthCloudFunction("setAppPlanUnified", body);
    if (response === undefined) return { ok: false, status: 500 };
    const { ok, status } = response;
    const responseBody = await response.json();
    return { ...responseBody, ok, status };
}

export async function transferAppToOrganization(
    appID: string,
    organizationID: string,
    confirmed: boolean,
    asPublicApp: boolean
): Promise<TransferAppToOrganizationResponse | undefined> {
    const body: TransferAppToOrganizationBody = { appID, organizationID, confirmed, asPublicApp };
    const response = await getAppFacilities().callAuthCloudFunction("transferAppToOrganization", body);
    if (response === undefined) return TransferAppToOrganizationResponse.ErrorOther;
    if (response.ok) return undefined;

    const result = await response.json();
    if (hasOwnProperty(result, "response") && typeof result.response === "string") {
        return result.response as TransferAppToOrganizationResponse;
    }
    return TransferAppToOrganizationResponse.ErrorOther;
}

export async function reloadPublishedAppDataFromSheet(
    appID: string,
    checkCompatibilityIssues: boolean,
    didReconnectSpreadsheet: boolean
): Promise<Response | undefined> {
    const requestBody: ReloadPublishedAppDataFromSheetBody = {
        appID,
        checkCompatibilityIssues,
        didReconnectSpreadsheet,
    };
    return await getAppFacilities().callAuthCloudFunction("reloadPublishedAppDataFromSheet", requestBody);
}

export async function setOrgOnboarding(requestBody: SetOrgOnboardingBody): Promise<Response | undefined> {
    return await getAppFacilities().callAuthCloudFunction("setOrgOnboarding", requestBody);
}

export async function finishOnboarding(requestBody: FinishOnboardingBody): Promise<Response | undefined> {
    return await getAppFacilities().callAuthCloudFunction("finishOnboarding", requestBody);
}
